import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Download, Upload, Server, Loader2, HardDrive, CloudUpload, Info } from "lucide-react";
import { api, errMsg } from "../lib/api";
import { fmtDate } from "../lib/format";
import { useAuth } from "../context/AuthContext";
import { PageWrap } from "../components/common";
import { ConfirmDialog } from "../components/ConfirmDialog";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { PasswordInput } from "../components/ui/password-input";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import { FolderBackup } from "../components/FolderBackup";
import { IS_DESKTOP } from "../lib/desktop";

const Card = ({ icon: Icon, title, desc, children }) => (
  <div className="rounded-2xl border bg-card p-5 space-y-4">
    <div className="flex gap-3">
      <div className="h-10 w-10 rounded-xl bg-primary/15 text-primary grid place-items-center shrink-0"><Icon className="h-5 w-5" /></div>
      <div><h2 className="font-display font-bold">{title}</h2><p className="text-sm text-muted-foreground">{desc}</p></div>
    </div>
    {children}
  </div>
);

const LocalBackup = () => {
  const { setStore } = useAuth();
  const fileRef = useRef(null);
  const [pending, setPending] = useState(null);
  const [busy, setBusy] = useState(false);
  const download = async () => {
    setBusy(true);
    try {
      const { data } = await api.get("/backup/export");
      const blob = new Blob([JSON.stringify(data)], { type: "application/json" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `mufapos-backup-${new Date().toISOString().slice(0, 19).replace(/[:T]/g, "-")}.json`;
      a.click();
      localStorage.setItem("pos_last_local_backup", new Date().toISOString());
      toast.success("Backup tersimpan di komputer Anda");
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };
  const pick = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    try {
      const data = JSON.parse(await f.text());
      if (data.app !== "MUFA POS") throw new Error("Bukan file backup MUFA POS");
      setPending(data);
    } catch (err) {
      toast.error(err.message);
    }
  };
  const restore = async () => {
    try {
      const { data } = await api.post("/backup/import", pending);
      toast.success(`Data dipulihkan: ${data.restored.products} produk, ${data.restored.transactions} transaksi`);
      setStore((await api.get("/settings")).data);
    } catch (e) {
      toast.error(errMsg(e));
    }
    setPending(null);
  };
  const last = localStorage.getItem("pos_last_local_backup");
  return (
    <Card icon={HardDrive} title="Backup ke Komputer" desc="Unduh seluruh data toko (produk, kategori, transaksi, shift, role & profil toko) sebagai file .json.">
      <div className="flex flex-wrap gap-2">
        <Button onClick={download} disabled={busy} data-testid="backup-download-btn">{busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Download className="h-4 w-4 mr-2" />}Unduh Backup</Button>
        <Button variant="outline" onClick={() => fileRef.current?.click()} data-testid="backup-restore-btn"><Upload className="h-4 w-4 mr-2" />Pulihkan dari File</Button>
        <input ref={fileRef} type="file" accept="application/json,.json" className="hidden" onChange={pick} data-testid="backup-file-input" />
      </div>
      <p className="text-xs text-muted-foreground">{last ? `Backup terakhir dari perangkat ini: ${fmtDate(last)}` : "Belum pernah backup dari perangkat ini."}</p>
      <ConfirmDialog open={!!pending} onOpenChange={(o) => !o && setPending(null)} title="Pulihkan data dari backup?" confirmText="Pulihkan"
        description={`Data produk, kategori, transaksi & shift toko saat ini akan DIGANTI dengan isi backup (${pending ? fmtDate(pending.exported_at) : ""}). Akun pengguna tidak berubah.`} onConfirm={restore} />
    </Card>
  );
};

const NasBackup = () => {
  const [f, setF] = useState(null);
  const [busy, setBusy] = useState("");
  useEffect(() => { api.get("/backup/nas").then((r) => setF({ enabled: false, url: "", username: "", password: "", folder: "mufapos-backup", auto_on_close_shift: false, ...r.data })); }, []);
  if (!f) return null;
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const save = async () => {
    setBusy("save");
    try {
      const { data } = await api.put("/backup/nas", { ...f, password: f.password || "" });
      setF({ ...f, ...data, password: "" });
      toast.success("Pengaturan NAS disimpan");
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy("");
    }
  };
  const run = async () => {
    setBusy("run");
    try {
      const { data } = await api.post("/backup/nas/run");
      toast.success(`Backup terkirim ke NAS: ${data.file}`);
      setF((x) => ({ ...x, last_backup_at: new Date().toISOString(), last_backup_file: data.file }));
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy("");
    }
  };
  return (
    <Card icon={Server} title="Backup ke Cloud / NAS (WebDAV)" desc="Kirim backup ke NAS Anda (Synology, QNAP, TrueNAS, Nextcloud) melalui WebDAV.">
      <div className="grid sm:grid-cols-2 gap-4">
        <div className="space-y-1.5 sm:col-span-2"><Label>URL WebDAV</Label><Input value={f.url} onChange={set("url")} placeholder="https://nas-anda.myds.me:5006" data-testid="nas-url-input" /></div>
        <div className="space-y-1.5"><Label>Username</Label><Input value={f.username} onChange={set("username")} data-testid="nas-username-input" /></div>
        <div className="space-y-1.5"><Label>Password</Label><PasswordInput value={f.password} onChange={set("password")} placeholder={f.has_password ? "•••••• (tersimpan)" : ""} data-testid="nas-password-input" /></div>
        <div className="space-y-1.5 sm:col-span-2"><Label>Folder Tujuan</Label><Input value={f.folder} onChange={set("folder")} data-testid="nas-folder-input" /></div>
      </div>
      <label className="flex items-center justify-between text-sm"><span>Aktifkan backup NAS</span><Switch checked={f.enabled} onCheckedChange={(v) => setF({ ...f, enabled: v })} data-testid="nas-enabled-switch" /></label>
      <label className="flex items-center justify-between text-sm"><span>Backup otomatis setiap tutup shift</span><Switch checked={f.auto_on_close_shift} onCheckedChange={(v) => setF({ ...f, auto_on_close_shift: v })} data-testid="nas-auto-switch" /></label>
      <div className="flex flex-wrap gap-2">
        <Button variant="outline" onClick={save} disabled={!!busy} data-testid="nas-save-btn">{busy === "save" && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}Simpan</Button>
        <Button onClick={run} disabled={!!busy || !f.url} data-testid="nas-run-btn">{busy === "run" ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <CloudUpload className="h-4 w-4 mr-2" />}Backup ke NAS Sekarang</Button>
      </div>
      <p className="text-xs text-muted-foreground" data-testid="nas-last-backup">{f.last_backup_at ? `Terakhir: ${fmtDate(f.last_backup_at)} · ${f.last_backup_file}` : "Belum ada backup ke NAS."}</p>
      <div className="rounded-xl bg-muted/60 p-3 text-xs text-muted-foreground flex gap-2">
        <Info className="h-4 w-4 shrink-0" />
        <span>Aktifkan layanan WebDAV di NAS (Synology: Package Center → WebDAV Server, port HTTPS 5006). NAS harus dapat diakses dari internet (DDNS / QuickConnect / port forwarding) karena backup dikirim dari server MUFA POS.</span>
      </div>
    </Card>
  );
};

export default function Backup() {
  return (
    <PageWrap>
      <div className="grid xl:grid-cols-2 gap-6 items-start">
        {IS_DESKTOP && (
          <Card icon={HardDrive} title="Backup Otomatis ke Folder Komputer" desc="Pilih folder (misal di drive D:) — backup langsung tersimpan otomatis di sana.">
            <FolderBackup />
          </Card>
        )}
        <LocalBackup />
        <NasBackup />
      </div>
    </PageWrap>
  );
}
